import { useState } from "react";
import { CheckCircle2, Ban, BarChart3 } from "lucide-react";
import StoreReceiptsMap from "@/components/StoreReceiptsMap";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

export function ReviewActions({ r, onDone }) {
  const [mode, setMode] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      if (mode === "approve") { const res = await api.receiptOcrApprove(r.id, note); toast.success(`Scontrino approvato: giocata ${res.play?.result === "win" ? "VINCENTE" : "registrata"}${r.phone || r.customer_email ? ", cliente avvisato" : ""}`); }
      else { await api.receiptOcrVoid(r.id, note); toast.success("Scontrino annullato"); }
      setMode(null); setNote(""); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); } finally { setBusy(false); }
  };
  if (r.review) return <p className="text-[11px] text-muted-foreground" data-testid={`receipt-review-info-${r.id}`}>{r.review.action === "approved" ? "Approvato manualmente" : "Annullato"} da {r.review.by} · {new Date(r.review.at).toLocaleString("it-IT")}{r.review.note ? ` · ${r.review.note}` : ""}</p>;
  return (
    <div className="pt-1" data-testid={`receipt-review-${r.id}`}>
      {!mode ? (
        r.result === "rejected"
          ? <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-emerald-400 text-emerald-700" onClick={() => setMode("approve")} data-testid={`receipt-approve-${r.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Approva manualmente</Button>
          : <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-muted-foreground hover:text-destructive" onClick={() => setMode("void")} data-testid={`receipt-void-${r.id}`}><Ban className="h-3.5 w-3.5" /> Annulla scontrino</Button>
      ) : (
        <div className="flex gap-2 items-center flex-wrap">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={mode === "approve" ? "Motivo approvazione (es. OCR errato, totale leggibile a mano)" : "Motivo annullamento"} className="h-8 text-xs flex-1 min-w-[220px] bg-white" data-testid={`receipt-review-note-${r.id}`} />
          <Button size="sm" className="h-8 text-xs" variant={mode === "approve" ? "default" : "destructive"} disabled={busy} onClick={go} data-testid={`receipt-review-confirm-${r.id}`}>{mode === "approve" ? "Conferma approvazione" : "Conferma annullamento"}</Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setMode(null)}>Annulla</Button>
        </div>
      )}
    </div>
  );
}

const Bar = ({ items, testId }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  return <ul className="space-y-1.5" data-testid={testId}>{items.length === 0 && <li className="text-xs text-muted-foreground">—</li>}{items.map((i) => <li key={i.name} className="text-xs"><div className="flex justify-between gap-2"><span className="truncate">{i.name}</span><span className="text-muted-foreground shrink-0">{i.count}</span></div><div className="h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-[var(--nc-blue)]" style={{ width: `${Math.round((i.count / max) * 100)}%` }} /></div></li>)}</ul>;
};

export function ReceiptStats({ s }) {
  const eur = (v) => `${Number(v || 0).toFixed(2)} €`;
  return (
    <Card className="p-5 bg-card border-border space-y-4" data-testid="receipt-stats">
      <h3 className="font-display font-bold text-lg flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Statistiche scontrini</h3>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[["Ricevuti", s.total, "total"], ["Validi", s.valid, "valid"], ["Scartati", s.rejected, "rejected"], ["Scontrino medio", eur(s.avg_total), "avg"], ["Spesa totale validata", eur(s.sum_total), "sum"]].map(([l, v, t]) => (
          <div key={t} className="rounded-lg bg-secondary/40 p-3" data-testid={`receipt-stat-${t}`}><div className="text-[11px] text-muted-foreground">{l}</div><div className="font-display font-bold text-xl">{v}</div></div>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <div><p className="text-xs font-semibold mb-1.5">Negozi più frequenti</p><Bar items={s.top_stores} testId="receipt-top-stores" /></div>
        <div><p className="text-xs font-semibold mb-1.5">Prodotti più acquistati (quantità)</p><Bar items={s.top_products} testId="receipt-top-products" /></div>
        <div><p className="text-xs font-semibold mb-1.5">Motivi di scarto</p><Bar items={s.reject_reasons} testId="receipt-reject-reasons" /></div>
      </div>
      {s.store_rank?.length > 0 && (
        <div data-testid="receipt-store-rank" className="space-y-3">
          <p className="text-xs font-semibold">Mappa sedi · bolle proporzionali agli scontrini validati</p>
          <StoreReceiptsMap rows={s.store_rank} />
          <p className="text-xs font-semibold mb-1.5">Classifica sedi · scontrini validati e spesa media</p>
          <div className="overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-1 pr-2">#</th><th className="py-1 pr-2">Sede</th><th className="py-1 pr-2">Città</th><th className="py-1 pr-2 text-right">Scontrini</th><th className="py-1 pr-2 text-right">Spesa media</th><th className="py-1 text-right">Spesa totale</th></tr></thead>
            <tbody>{s.store_rank.map((row, i) => {
              const max = s.store_rank[0].count || 1;
              return (
                <tr key={row.store_id || "none"} className="border-b border-border/60" data-testid={`store-rank-row-${i}`}>
                  <td className="py-1.5 pr-2 font-bold text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-2"><b>{row.name}</b><div className="mt-0.5 h-1.5 rounded bg-secondary"><div className="h-1.5 rounded bg-primary" style={{ width: `${Math.round((row.count / max) * 100)}%` }} /></div></td>
                  <td className="py-1.5 pr-2 text-muted-foreground">{[row.city, row.province].filter(Boolean).join(" ")}</td>
                  <td className="py-1.5 pr-2 text-right font-semibold">{row.count}</td>
                  <td className="py-1.5 pr-2 text-right">{eur(row.avg_total)}</td>
                  <td className="py-1.5 text-right">{eur(row.sum_total)}</td>
                </tr>
              );
            })}</tbody>
          </table></div>
        </div>
      )}
    </Card>
  );
}
